import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Expense : famille "frais engagé".
 *
 * Frais engagé par une personne ou pour une activité, à valider puis rembourser
 * ou refacturer, avec justificatif. Construite sur le modèle "famille + type" de
 * la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète du frais ("repas",
 * "transport", "materiel") : il connaît la famille générique `expense`. Le
 * vocabulaire est porté par le champ `type` (chaîne libre déclarée par l'app).
 * L'état du circuit (à valider, validé, remboursé, refacturé) vit dans `status`,
 * pas dans le type.
 *
 * Le montant `amount` est le cœur du frais, donc requis (comme payment). Les
 * liens vers d'autres familles (worker, project, invoice, payment, document)
 * sont de simples ids optionnels, jamais un import d'un autre schéma : un frais
 * est engagé PAR une personne OU pour une activité, donc ni `workerId` ni
 * `projectId` n'est forcé. `receiptDocumentId` porte le justificatif ;
 * `invoiceId` sert la refacturation, `paymentId` le remboursement.
 *
 * Sécurité / vie privée (cf. fiche) : un frais est une donnée personnelle
 * (owner + valideur + comptabilité + manager du projet imputé), sans lecture
 * org-wide à terme. En v0 la famille reste néanmoins org-wide (même doctrine
 * qu'asset) ; l'accès fin par rôle viendra après, hors de ce schéma.
 *
 * Convention de patron (cf. asset) :
 *   - `expenseCoreFields`        : les champs MÉTIER seuls (sans champs standard).
 *   - `ExpenseSchema`            : famille complète = champs standard + métier.
 *   - `ExpenseCreateInputSchema` : ce que l'appelant fournit à la création
 *     (champs métier ; les champs standard sont remplis par le SDK). Sert à
 *     VALIDER l'entrée AVANT toute écriture Firestore.
 */

/** Champs métier de la famille expense (hors champs standard du socle). */
export const expenseCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "repas", "transport", "materiel"). */
  type: z.string().min(1),
  /** Montant du frais engagé. Cœur de la famille, requis. */
  amount: z.number(),
  /** Personne ayant engagé le frais (référence `worker`). Optionnel (un frais peut viser une activité). */
  workerId: z.string().min(1).optional(),
  /** Catégorie comptable / analytique, chaîne libre pilotée par l'app (ex. "restauration", "hebergement"). Optionnel. */
  category: z.string().min(1).optional(),
  /** Date à laquelle le frais a été engagé. Optionnel. */
  incurredAt: z.date().optional(),
  /** Justificatif rattaché (référence `document`). Optionnel. */
  receiptDocumentId: z.string().min(1).optional(),
  /** Frais refacturable à un client / projet. Optionnel. */
  rebillable: z.boolean().optional(),
  /** Activité / projet imputé (référence `project`). Optionnel. */
  projectId: z.string().min(1).optional(),
  /** Facture de refacturation si le frais a été refacturé (référence `invoice`). Optionnel. */
  invoiceId: z.string().min(1).optional(),
  /** Paiement de remboursement si le frais a été remboursé (référence `payment`). Optionnel. */
  paymentId: z.string().min(1).optional(),
  /** État du circuit, chaîne libre pilotée par l'app (ex. "a-valider", "valide", "rembourse", "refacture"). Optionnel. */
  status: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille expense (champs standard + champs métier). */
export const ExpenseSchema = z.object({
  ...baseEntityFields,
  ...expenseCoreFields,
});

/** Type d'un expense. */
export type Expense = z.infer<typeof ExpenseSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const ExpenseCreateInputSchema = z.object({
  ...expenseCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un expense. */
export type ExpenseCreateInput = z.infer<typeof ExpenseCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const ExpenseUpdateInputSchema = z
  .object({ ...expenseCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un expense. */
export type ExpenseUpdateInput = z.infer<typeof ExpenseUpdateInputSchema>;
